// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  CssBaseline,
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Skeleton,
  Stack
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { getStorefrontProducts } from '../db';
import { useTheme } from '@mui/material/styles';
import { useSeo, useJsonLd, SITE_URL } from '../seo';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Wheat } from 'lucide-react';
import { DISPLAY_FONT, SITE } from '../siteInfo';
import { visibleOnStorefront } from '../products';
import { photoUrl, productPhotos } from '../photos';
import SiteSlideshow from '../components/SiteSlideshow';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProducts() {
      try {
        // Every product in the catalogue, in the order set in the dashboard.
        const data = await getStorefrontProducts();
        const mappedProducts = data.filter(visibleOnStorefront).map((row) => ({
          id: row.id,
          name: row.name || '',
          price: row.price,
          description: row.description || '',
          showcaseDescription: row.showcaseDescription || '',
          showcaseDescMode: row.showcaseDescMode || 'tied',
          isForSale: row.isForSale !== false,
          showcase: row.isForSale === false && row.showcase === true,
          available: row.available,
          // Set in the admin dashboard; the built-in photos until they are.
          photos: productPhotos(row),
        }));
        setProducts(mappedProducts);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching products", err);
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const location = useLocation();

  // The same words as the static tags in public/index.html, so Google and a
  // shared link describe the shop alike.
  useSeo({
    title: 'Crestancia | Hay Bales & Shavings in Sasolburg',
    description:
      'Teff, lucerne, eragrostis and oat hay bales, and shavings, from Crestancia in Sasolburg. Order online and pay securely.',
    path: '/',
  });

  // Prices and stock come from the database, so the structured data is built
  // from what the page is actually showing rather than a second, drifting copy.
  useJsonLd(
    'ld-products',
    products.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: products.map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Product',
              name: product.name,
              description: product.description,
              // Required for a valid Product. Resolved against the canonical
              // host so the URL stays right even when the page is served from
              // the firebaseapp.com mirror.
              image: (product.photos.length
                ? product.photos.slice(0, 1).map((photo) => photoUrl(photo, 1280))
                : ['/social-card.jpg']
              ).map((src) => new URL(src, SITE_URL).href),
              brand: { '@type': 'Brand', name: SITE.name },
              offers: {
                '@type': 'Offer',
                url: `${SITE_URL}/`,
                price: product.price,
                priceCurrency: 'ZAR',
                // Read off the same stock figure the card shows, rather than
                // telling Google everything is always in stock.
                availability:
                  product.isForSale === false
                    ? 'https://schema.org/Discontinued'
                    : product.available === 0
                    ? 'https://schema.org/OutOfStock'
                    : 'https://schema.org/InStock',
              },
            },
          })),
        }
      : null,
  );

  // The header links to /home#products and /home#contact from every page, so
  // hash has to be honoured on arrival as well as on a click. `loading` is a
  // dependency because the products section is short until the cards land, and
  // scrolling to it before then stops well above where the reader expects.
  useEffect(() => {
    if (!location.hash) return;
    const target = document.querySelector(location.hash);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.key, location.hash, loading]);

  const theme = useTheme();

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header />
        <Box sx={{ flexGrow: 1 }}>
          {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#fff',
            overflow: 'hidden',
            // Tall enough to feel like a field, short enough that the products
            // below stay within reach of the first scroll.
            minHeight: { xs: 440, sm: 500, md: '62vh' },
            px: 3,
            py: { xs: 8, md: 10 },
            // The background slideshow is set in the dashboard and drawn by
            // SiteSlideshow beneath everything here, which the isolation keeps
            // inside this box.
            isolation: 'isolate',
            // In daylight the photograph carries itself. At night it is the one
            // bright thing on a dark page, so a scrim settles it back down and
            // keeps the type on top of it readable either way. The bottom stop
            // matches the page behind it, so the photo sinks into the section
            // below instead of ending on a hard line.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                theme.palette.mode === 'dark'
                  ? `linear-gradient(180deg, rgba(20,22,15,0.58) 0%, rgba(20,22,15,0.72) 55%, ${theme.palette.background.default} 100%)`
                  : `linear-gradient(180deg, rgba(28,32,18,0.42) 0%, rgba(28,32,18,0.52) 55%, ${theme.palette.background.default} 100%)`,
            },
          }}
        >
          <SiteSlideshow slot="hero" />
          <Box sx={{ position: 'relative', maxWidth: 760 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1.25}
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ width: 28, height: 2, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
              <Wheat size={18} strokeWidth={1.9} />
              <Box sx={{ width: 28, height: 2, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
            </Stack>

            <Typography
              variant="h1"
              sx={{
                fontFamily: DISPLAY_FONT,
                fontWeight: 600,
                letterSpacing: '-0.025em',
                lineHeight: 1.05,
                fontSize: { xs: '2.4rem', sm: '3.2rem', md: '4rem' },
                textShadow: '0 2px 24px rgba(0,0,0,0.45)',
                mb: 2,
              }}
            >
              Welcome to Crestancia
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.28rem' },
                lineHeight: 1.6,
                maxWidth: 560,
                mx: 'auto',
                color: 'rgba(255,255,255,0.9)',
                textShadow: '0 1px 12px rgba(0,0,0,0.4)',
              }}
            >
              Experience premium farm products crafted with excellence.
            </Typography>

            {/* The hero used to end on a full stop with nowhere to go. */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.75}
              justifyContent="center"
              sx={{ mt: 4.5 }}
            >
              <Button
                component={RouterLink}
                to="/home#products"
                variant="contained"
                size="large"
                disableElevation
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                }}
              >
                Shop products
              </Button>
              <Button
                component={RouterLink}
                to="/home#contact"
                size="large"
                sx={{
                  px: 3.5,
                  py: 1.5,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.45)',
                  backdropFilter: 'blur(6px)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  '&:hover': {
                    borderColor: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                  },
                }}
              >
                Get in touch
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Shop Section */}
        <Container id="products" maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, scrollMarginTop: '96px' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 1.5,
              }}
            >
              Shop
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}
            >
              Our Products
            </Typography>
            {/* A stalk between two rules — the olive keeps the brand in the
                heading now that the words themselves are plain ink. */}
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mt: 2.5 }}>
              <Box sx={{ width: 46, height: 2, borderRadius: 1, backgroundColor: 'primary.main', opacity: 0.35 }} />
              <Box sx={{ display: 'flex', color: 'primary.main' }}>
                <Wheat size={19} strokeWidth={1.9} />
              </Box>
              <Box sx={{ width: 46, height: 2, borderRadius: 1, backgroundColor: 'primary.main', opacity: 0.35 }} />
            </Stack>
          </Box>
          <Grid container spacing={3}>
            {loading
              ? [1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid item key={i} xs={12} sm={6} lg={4}>
                    <Skeleton variant="rounded" height={430} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))
              : products.map((product, index) => (
                  <Grid item key={product.id} xs={12} sm={6} lg={4}>
                    <ProductCard product={product} position={index} />
                  </Grid>
                ))}
          </Grid>
        </Container>
      </Box>
      <Footer />
      </Box>
    </>
  );
};

export default Home;
